import { ShopPreviewConfig } from '../components/ShopHtmlPreview';
import ShopItemDto, { ShopDisplaySection } from '../models/dtos/ShopItemDto';

export const FIRST_PURCHASE_STORE_CODE = 'new_shop_small_banner';

export function isFirstPurchaseContext(
    shopItem: Pick<ShopItemDto, 'displaySection' | 'storeCode'>,
): boolean {
    return shopItem.displaySection === 'FIRST_PURCHASE'
        || shopItem.storeCode === FIRST_PURCHASE_STORE_CODE;
}

export function getShopEditorTitle(isEditMode: boolean, isFirstPurchase: boolean): string {
    if (isFirstPurchase) {
        return isEditMode ? 'Edit First Purchase Offer' : 'Add First Purchase Offer';
    }
    return isEditMode ? 'Edit shop item' : 'Add new shop item';
}

export function getShopEditorSubtitle(isFirstPurchase: boolean): string | null {
    if (!isFirstPurchase) return null;
    return 'Configure the welcome banner shown at the top of the shop for users who have never made a purchase.';
}

export function getShopEditorDetailsHeading(isFirstPurchase: boolean): string {
    return isFirstPurchase ? 'First purchase offer details:' : 'Shop item details:';
}

export function getHtmlContentFieldLabel(isFirstPurchase: boolean): string {
    return isFirstPurchase
        ? 'First purchase HTML template (HTML_INLINE)'
        : 'HTML content (HTML_INLINE)';
}

export function getHtmlPreviewCaption(section?: ShopDisplaySection): string {
    if (section === 'FIRST_PURCHASE') return 'First purchase banner preview (mobile card)';
    return 'Live preview (mobile card)';
}

export function getPreviewPanelTitle(section?: ShopDisplaySection): string {
    if (section === 'FIRST_PURCHASE') return 'First purchase preview:';
    return 'Preview:';
}

export function getSectionBadgeLabel(section?: ShopDisplaySection): string | null {
    switch (section) {
        case 'FIRST_PURCHASE': return 'First Purchase';
        case 'DAILY_DEALS': return 'Daily Deals';
        case 'BUNDLES': return 'Bundles';
        case 'COIN_PACKS': return 'Coin Packs';
        case 'FREE_REWARDS': return 'Free Rewards';
        default: return null;
    }
}

export const FIRST_PURCHASE_PREVIEW_CONFIG: ShopPreviewConfig = {
    language: 'en',
    buyLabel: 'Claim $4.99',
    oldPrice: '$19.99',
    discountPercent: 0,
    videoTitle: 'Watch & earn',
    videoSubtitle: 'Free betcoins reward',
    multiplier: '3X',
    unitPriceLabel: '',
};

export const FIRST_PURCHASE_SAMPLE_TITLES: Record<string, string> = {
    en: '3X BONUS',
};

export function buildFirstPurchasePreviewItem(shopItem: ShopItemDto): ShopItemDto {
    const coins = shopItem.instantCoins || shopItem.betcoins || 300_000;
    const offerExpiresAt = shopItem.offerExpiresAt
        || new Date(Date.now() + 46 * 60 * 60 * 1000).toISOString();
    return {
        ...shopItem,
        displaySection: 'FIRST_PURCHASE',
        instantCoins: coins,
        betcoins: coins,
        visualPrice: shopItem.visualPrice || '4.99',
        offerExpiresAt,
    };
}

export function resolvePreviewBuyLabel(
    isFirstPurchase: boolean,
    visualPrice?: string,
): string {
    if (!isFirstPurchase) return 'Buy now';
    if (visualPrice) return `Claim $${visualPrice}`;
    return FIRST_PURCHASE_PREVIEW_CONFIG.buyLabel;
}

export function resolvePreviewTitles(
    isFirstPurchase: boolean,
    titles: Record<string, string>,
    titlesCount: number,
): Record<string, string> {
    if (isFirstPurchase && titlesCount === 0) {
        return FIRST_PURCHASE_SAMPLE_TITLES;
    }
    return titles;
}

export function resolvePreviewHtmlContent(
    shopItem: ShopItemDto,
    templateForSection: (section: ShopDisplaySection) => string,
): string {
    if (shopItem.htmlContent?.trim()) {
        return shopItem.htmlContent;
    }
    const section = shopItem.displaySection ?? 'COIN_PACKS';
    if (section === 'FIRST_PURCHASE' || shopItem.presentationMode === 'HTML_INLINE') {
        return templateForSection(section);
    }
    return '';
}

export function getFirstPurchaseHelperText(): string {
    return 'This SKU is for first-time buyers only. Enable "First-time buyers only" so the offer is hidden after the user\'s first shop purchase. Typical store code: new_shop_small_banner.';
}
